import { useMemo } from 'react';
import { AlertTriangle, ArrowRight, Ban, CalendarClock, Inbox, Layers3, UserCheck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useLocalization } from '@/hooks/useLocalization';
import { useTodayData } from '@/hooks/useTodayData';
import { usePortfolioProjects } from '@/hooks/usePortfolioProjects';
import { useCockpitData } from '@/hooks/useCockpitData';
import { healthTone, summarisePortfolio, worstFirst, type HealthTone } from '@/lib/pmoSummary';
import { cockpitSummary, type CockpitTask } from '@/lib/homeSummaries';
import { HomeActions, HomeEmpty, HomePanel, HomeRow, StatRow, StatTile } from './HomeFrame';

interface Props { onNavigate: (view: string, params?: Record<string, string>) => void; }

const TONE_CLASS: Record<HealthTone, string> = { 'on-track': 'bg-success/10 text-foreground', 'at-risk': 'bg-warning/15 text-foreground', critical: 'bg-destructive/10 text-destructive' };
const TONE_LABEL: Record<HealthTone, string> = { 'on-track': 'On track', 'at-risk': 'At risk', critical: 'Critical' };

/**
 * Project manager home: what is blocked, what is late, what is assigned to me and who is waiting for a reply.
 * The heading comes from the shell (roleHome).
 */
const ProjectCockpit = ({ onNavigate }: Props) => {
  const { t, tx, shortDate, ago } = useLocalization();
  const today = useTodayData();
  const projectsQuery = usePortfolioProjects();
  const c = useCockpitData();

  const allProjects = useMemo(() => projectsQuery.data ?? [], [projectsQuery.data]);
  const portfolio = useMemo(() => summarisePortfolio(allProjects), [allProjects]);
  const projects = useMemo(() => worstFirst(allProjects).slice(0, 6), [allProjects]);
  const summary = useMemo(() => cockpitSummary(c.tasks, c.myName), [c.tasks, c.myName]);
  const projectName = useMemo(() => {
    const m = new Map<string, string>();
    allProjects.forEach((p) => { if (p.external_id) m.set(p.external_id, p.name); });
    return (task: CockpitTask) => (task.project_external_id ? m.get(task.project_external_id) ?? null : null);
  }, [allProjects]);
  const escalated = today.emails.filter((e) => e.escalation_flag).length;
  // A role may be allowed to read tasks but not projects (the projects_sync policy excludes plain project managers).
  // Then the project list is empty while tasks clearly belong to projects: say that, instead of "no projects synced".
  const projectsInTasks = useMemo(() => new Set(c.tasks.map((task) => task.project_external_id).filter(Boolean)).size, [c.tasks]);
  const projectsHidden = allProjects.length === 0 && projectsInTasks > 0 && !projectsQuery.isError;

  if (c.isLoading || projectsQuery.isLoading || today.isLoading) {
    return (
      <div className="space-y-6" aria-busy="true" aria-label={t('Loading…')}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}</div>
        <div className="grid gap-6 lg:grid-cols-3"><Skeleton className="h-72 lg:col-span-2" /><Skeleton className="h-72" /></div>
      </div>
    );
  }

  const taskRow = (task: CockpitTask, icon: JSX.Element) => (
    <HomeRow key={task.id} onClick={() => onNavigate('deliverHub')}>
      {icon}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{task.title}</span>
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
          {[projectName(task), task.assignee ?? t('Unassigned'), task.due_date ? t('due {0}', { 0: shortDate(task.due_date) }) : null].filter(Boolean).join(' · ')}
        </span>
      </span>
      {task.priority && <Badge variant="outline" className="shrink-0">{tx(task.priority)}</Badge>}
    </HomeRow>
  );

  return (
    <div className="space-y-6">
      <HomeActions>
        <Button variant="outline" onClick={() => onNavigate('inbox')}>{t('Open inbox')}</Button>
        <Button onClick={() => onNavigate('projects')}>{t('Open projects')}</Button>
      </HomeActions>

      <StatRow>
        <StatTile
          label={t('Blocked tasks')}
          icon={<Ban className="h-4 w-4" />}
          value={summary.blocked.length}
          tone={summary.blocked.length > 0 ? 'critical' : 'default'}
          hint={summary.blocked.length ? t('Work is stalled until these are unblocked.') : t('Nothing is blocked.')}
          onClick={() => onNavigate('deliverHub')}
        />
        <StatTile
          label={t('Overdue tasks')}
          icon={<CalendarClock className="h-4 w-4" />}
          value={summary.overdue.length}
          tone={summary.overdue.length > 0 ? 'warning' : 'default'}
          hint={summary.overdue[0]?.due_date ? t('oldest was due {0}', { 0: shortDate(summary.overdue[0].due_date) }) : t('Nothing is overdue.')}
          onClick={() => onNavigate('deliverHub')}
        />
        <StatTile
          label={t('Projects')}
          icon={<Layers3 className="h-4 w-4" />}
          value={projectsHidden ? projectsInTasks : portfolio.total}
          tone={portfolio.critical > 0 ? 'critical' : portfolio.atRisk > 0 ? 'warning' : 'default'}
          hint={projectsHidden ? t('with open tasks; health is not visible to your role')
            : portfolio.total ? t('{0} at risk, {1} critical', { 0: portfolio.atRisk, 1: portfolio.critical }) : t('No projects synced yet.')}
          onClick={() => onNavigate('projects')}
        />
        <StatTile
          label={t('Replies owed')}
          icon={<Inbox className="h-4 w-4" />}
          value={today.unreadCount}
          tone={escalated > 0 ? 'critical' : 'default'}
          hint={today.unreadCount ? t('{0} escalated among the latest', { 0: escalated }) : t('No unread messages.')}
          onClick={() => onNavigate('inbox')}
        />
      </StatRow>

      <div className="grid gap-6 lg:grid-cols-3">
        <HomePanel className="lg:col-span-2" title={t('Blocked work')} hint={t('Unblock these first; everything behind them is waiting.')} action={<Badge variant="outline">{summary.blocked.length}</Badge>}>
          {summary.blocked.length === 0 ? (
            <HomeEmpty>{c.tasksFailed ? t('Tasks could not be loaded.') : t('Nothing is blocked.')}</HomeEmpty>
          ) : summary.blocked.slice(0, 6).map((task) => taskRow(task, <Ban className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />))}
        </HomePanel>

        <HomePanel title={t('Projects')} hint={t('Worst first. Open a project to see its risks.')} action={<Button variant="ghost" size="sm" onClick={() => onNavigate('projects')}>{t('View all')} <ArrowRight className="ms-1 h-4 w-4 rtl:rotate-180" /></Button>}>
          {projects.length === 0 ? (
            <HomeEmpty>{projectsQuery.isError ? t('Projects could not be loaded.') : projectsHidden ? t('Project details are not visible to your role. Ask an administrator if you need them.') : t('No projects synced yet.')}</HomeEmpty>
          ) : projects.map((p) => {
            const tone = healthTone(p.health);
            return (
              <HomeRow key={p.id} onClick={() => onNavigate('risks', { project: p.id })} label={t('{0}: {1}. Open its risks.', { 0: p.name, 1: t(TONE_LABEL[tone]) })}>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold">{p.name}</span>
                    <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium', TONE_CLASS[tone])}>{t(TONE_LABEL[tone])}</span>
                  </span>
                  <Progress value={p.progress ?? 0} className="mt-2 h-1.5" />
                </span>
                <span className="font-mono text-xs tabular-nums text-muted-foreground">{p.progress ?? 0}%</span>
              </HomeRow>
            );
          })}
        </HomePanel>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <HomePanel title={t('Overdue work')} hint={t('Oldest first.')} action={<Badge variant="outline">{summary.overdue.length}</Badge>}>
          {summary.overdue.length === 0 ? <HomeEmpty>{t('Nothing is overdue.')}</HomeEmpty>
            : summary.overdue.slice(0, 5).map((task) => taskRow(task, <AlertTriangle className="h-4 w-4 shrink-0 text-warning" aria-hidden="true" />))}
        </HomePanel>

        <HomePanel title={t('Assigned to me')} hint={c.myName ? t('Tasks whose assignee is {0}.', { 0: c.myName }) : undefined} action={<Badge variant="outline">{summary.mine.length}</Badge>}>
          {!c.myName ? (
            <HomeEmpty>{t('Set a display name in Settings so tasks can be matched to you.')}</HomeEmpty>
          ) : summary.mine.length === 0 ? (
            <HomeEmpty>{t('No open tasks are assigned to {0}.', { 0: c.myName })}</HomeEmpty>
          ) : summary.mine.slice(0, 5).map((task) => taskRow(task, <UserCheck className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />))}
        </HomePanel>

        <HomePanel title={t('Replies owed')} hint={t('Unread, escalations first.')} action={<Badge variant="outline">{today.unreadCount}</Badge>}>
          {today.emails.length === 0 ? <HomeEmpty>{t('No unread messages.')}</HomeEmpty> : today.emails.map((e) => (
            <HomeRow key={e.id} onClick={() => onNavigate('inbox')}>
              {e.escalation_flag ? <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" aria-label={t('Escalated')} /> : <Inbox className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{e.subject ?? t('(no subject)')}</span>
                <span className="mt-0.5 block truncate text-xs text-muted-foreground">{[e.sender_name, ago(e.received_at)].filter(Boolean).join(' · ')}</span>
              </span>
            </HomeRow>
          ))}
        </HomePanel>
      </div>
    </div>
  );
};

export default ProjectCockpit;
